import { useEffect, useRef } from "react";
import { useHideData } from "./useHideData";

// Shift + H toggles Privacy Mode from anywhere in the app.
//
// Never fires while the user is typing (Shift + H is a capital H in a text field). It DOES fire
// while a modal is open: the guard was first copied from TopbarSearch.js, which skips modals for
// its search shortcut, but for a privacy toggle that was exactly backwards — a modal is often the
// most data-dense thing on screen at the moment someone walks up. Plain `f` is taken by
// TopbarSearch; Shift + H is free.
//
// Mounted once, in src/App.js.
export const useHideDataShortcut = () => {
  // Deliberately no useTranslation here. This hook is mounted high in the tree, ABOVE every
  // <Suspense> boundary (they all live inside the layouts), and i18n.js loads namespaces with a
  // dynamic import() — a suspending useTranslation up here crashes the synchronous
  // ReactDOM.render with "A component suspended while responding to synchronous input". Since
  // the shortcut no longer shows a toast, it needs no strings at all.
  const { hidden, toggle } = useHideData();

  // Keep the latest values without re-binding the listener on every render.
  const stateRef = useRef({ hidden, toggle });
  stateRef.current = { hidden, toggle };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.toLowerCase() !== "h") return;

      const tag = e.target.tagName ? e.target.tagName.toLowerCase() : "";
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        e.target.isContentEditable;
      if (isTyping) return;

      e.preventDefault();
      // No toast: the shortcut exists to flip the mode quickly and often, so a notification on
      // every press is noise. The masked values themselves are the feedback — plus the check
      // mark on the Privacy Mode row in the profile dropdown.
      stateRef.current.toggle();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
};

export default useHideDataShortcut;
